import React, { useState, useCallback, useEffect, useRef } from 'react'
import type { Agent } from '@/services/api/models/Agent'
import { useAgents, type AgentFormData } from '@/hooks/useAgents'
import { useAgentSharing } from '@/hooks/useAgentSharing'
import { useAuthStore } from '@/stores/authStore'
import { useDebounce } from '@/hooks/useDebounce'
import { toast } from 'react-hot-toast'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'
import { InvitationsPanel } from '@/components/agents/InvitationsPanel'
import { SharedAgentsPanel } from '@/components/agents/SharedAgentsPanel'
import { AgentPermissionsModal } from '@/components/agents/AgentPermissionsModal'
import { generateEncryptionKey, isValidEncryptionKey } from '@/utils/keyGenerator'
import {
  getAgentOpenAISettings,
  saveAgentOpenAISettings,
  type AgentOpenAISettings,
  getAgentApiKey,
  saveAgentApiKey
} from '@/utils/agentEncryption'
import { isAgentOutdated, formatVersionDisplay } from '@/utils/versionUtils'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  WifiIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
  ServerIcon,
  CpuChipIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
  KeyIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  ShareIcon,
  BellIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { useAgentsPageStore } from '@/stores/agentsPageStore'

// Tab type is maintained in store

interface DeploymentState {
  showConfig: boolean
  type: 'docker' | 'docker-compose' | 'kubernetes'
  apiKey: string
}

// Memoized Agent Card Component to prevent unnecessary re-renders
interface AgentCardProps {
  agent: Agent
  currentUser: { id?: string } | null
  agentVersion: string | null
  deploymentState: DeploymentState
  onToggle: (id: string) => void
  onEdit: (agent: Agent, permission: 'owner' | 'write' | 'read') => void
  onViewPermissions: (agent: Agent) => void
  onDelete: (id: string) => void
  onDeploymentStateChange: (agentId: string, state: Partial<DeploymentState>) => void
  getAgentApiKey: (agentId: string) => string
  saveAgentApiKey: (agentId: string, key: string) => void
  getUserPermissionForAgent: (agent: Agent) => 'owner' | 'write' | 'read' | null
  formatDate: (date: string) => string
  generateDockerCommand: (agent: Agent, apiKey: string) => string
  generateDockerComposeConfig: (agent: Agent, apiKey: string) => string
  generateKubernetesConfig: (agent: Agent, apiKey: string) => string
}

const AgentCard = React.memo<AgentCardProps>(({ 
  agent, 
  currentUser: _currentUser,
  agentVersion,
  deploymentState,
  onToggle,
  onEdit,
  onViewPermissions,
  onDelete,
  onDeploymentStateChange,
  getAgentApiKey: getApiKey,
  saveAgentApiKey: saveApiKey,
  getUserPermissionForAgent,
  formatDate,
  generateDockerCommand,
  generateDockerComposeConfig,
  generateKubernetesConfig
}) => {
  const userPermission = getUserPermissionForAgent(agent)
  const canManage = userPermission === 'owner' || userPermission === 'write'
  const canEdit = true // All users can edit, but with different permissions
  const canDelete = canManage
  const canViewPermissions = userPermission === 'owner' || userPermission === 'write'
  const canDeploy = userPermission === 'owner' || userPermission === 'write'

  return (
    <div
      className={clsx(
        'bg-white dark:bg-gray-800 rounded-xl shadow-sm border transition-all duration-200 hover:shadow-lg overflow-hidden',
        agent.is_active
          ? 'border-green-200 dark:border-green-800 ring-1 ring-green-100 dark:ring-green-900/50'
          : 'border-gray-200 dark:border-gray-700'
      )}
    >
      {/* Card Header */}
      <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className={clsx(
                  'w-3 h-3 rounded-full',
                  agent.connection_status === 'connected' ? 'bg-green-500' :
                  agent.connection_status === 'error' ? 'bg-red-500' : 'bg-gray-400'
                )} />
                {agent.connection_status === 'connected' && (
                  <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping opacity-75" />
                )}
              </div>
              <div className="flex items-center space-x-2">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {agent.name}
                </h3>
                {/* Version Display */}
                {formatVersionDisplay(agent.agent_version ?? null) && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded-md">
                    {formatVersionDisplay(agent.agent_version ?? null)}
                  </span>
                )}
                {/* Outdated Indicator */}
                {agent.agent_version && agentVersion && isAgentOutdated(agent.agent_version, agentVersion) && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 rounded-md" title={`Agent version ${agent.agent_version} is outdated. Latest version: ${agentVersion}`}>
                    Update Available
                  </span>
                )}
              </div>
            </div>
            <span
              className={clsx(
                'inline-flex px-3 py-1 text-xs font-semibold rounded-full',
                agent.connection_status === 'connected' 
                  ? 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/30' :
                agent.connection_status === 'error'
                  ? 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/30'
                  : 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-700'
              )}
            >
              {agent.connection_status || 'Unknown'}
            </span>
            {/* Show Read label for read-only agents */}
            {userPermission === 'read' && (
              <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-900/30">
                Read Only
              </span>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            {/* Status Toggle - only for agents user can manage */}
            {canManage && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {agent.is_active ? 'Active' : 'Inactive'}
                </span>
                <button type="button"
                  onClick={() => onToggle(agent.id!)}
                  className={clsx(
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                    agent.is_active ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                  )}
                  title={agent.is_active ? 'Disable agent' : 'Enable agent'}
                >
                  <div
                    className={clsx(
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      agent.is_active ? 'translate-x-6' : 'translate-x-1'
                    )}
                  />
                </button>
              </div>
            )}
            
            {canManage && <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />}
            
            <div className="flex items-center space-x-1">
              {canEdit && (
                <button type="button"
                  onClick={() => onEdit(agent, userPermission!)}
                  className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all duration-200"
                  title="Edit agent"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
              )}
              {canViewPermissions && (
                <button type="button"
                  onClick={() => { onViewPermissions(agent) }}
                  className="relative p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all duration-200"
                  title={`Manage permissions${agent.pending_invitations_count ? ` (${agent.pending_invitations_count} pending invitation${agent.pending_invitations_count > 1 ? 's' : ''})` : ''}`}
                >
                  <UserGroupIcon className="w-4 h-4" />
                  {/* Pending invitations badge - left side */}
                  {agent.pending_invitations_count !== null && agent.pending_invitations_count !== undefined && agent.pending_invitations_count > 0 && (
                    <span className="absolute -top-1.5 -left-1 inline-flex items-center justify-center px-1 py-0.5 text-[10px] font-bold text-white bg-yellow-500 rounded-full min-w-[16px] h-[16px]">
                      {agent.pending_invitations_count}
                    </span>
                  )}
                  {/* Shared users count badge - right side */}
                  {agent.shared_users_count !== null && agent.shared_users_count !== undefined && agent.shared_users_count > 0 && (
                    <span className="absolute -top-1.5 -right-1 inline-flex items-center justify-center px-1 py-0.5 text-[10px] font-bold text-white bg-green-500 rounded-full min-w-[16px] h-[16px]">
                      {agent.shared_users_count + 1}
                    </span>
                  )}
                </button>
              )}
              {canDelete && (
                <button type="button"
                  onClick={() => { onDelete(agent.id!) }}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                  title="Delete agent"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-4">
        {/* Description */}
        {agent.description && (
          <div className="text-gray-600 dark:text-gray-400 mb-4 text-sm leading-relaxed whitespace-pre-wrap break-words">
            {agent.description}
          </div>
        )}
        
        {/* Agent Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="space-y-3">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Agent Information</h4>
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 dark:text-gray-400">ID:</span>
                  <code className="text-xs bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">{agent.id}</code>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Created:</span>
                  <span className="text-gray-900 dark:text-white">{formatDate(agent.created_at!)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Last Connected:</span>
                  <span className="text-gray-900 dark:text-white">{formatDate(agent.last_connected_at!)}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            {agent.capabilities && Object.keys(agent.capabilities).length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Capabilities</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(agent.capabilities).map((capability) => (
                    <span
                      key={capability}
                      className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md font-medium"
                    >
                      {capability}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Deployment Section - Only show for owner/write permissions */}
      {canDeploy && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="p-4">
            <div className="text-center mb-4">
              <div className="flex items-center justify-center mb-3">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Deploy Agent</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Get your agent running in seconds</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  const savedApiKey = getApiKey(agent.id!)
                  onDeploymentStateChange(agent.id!, { 
                    showConfig: !deploymentState.showConfig,
                    apiKey: savedApiKey
                  })
                }}
                className="inline-flex items-center space-x-2 px-6 py-3 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-4 focus:ring-primary-200 dark:focus:ring-primary-800 transition-all duration-200 shadow-sm"
              >
                <span>{deploymentState.showConfig ? 'Hide Configuration' : 'Show Configuration'}</span>
              </button>
            </div>

            {deploymentState.showConfig && (
              <div className="max-w-4xl mx-auto">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="space-y-4">
                    {/* Configuration Inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          🐳 Deployment Method
                        </label>
                        <select
                          value={deploymentState.type}
                          onChange={(e) => onDeploymentStateChange(agent.id!, { 
                            type: e.target.value as 'docker' | 'docker-compose' | 'kubernetes' 
                          })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        >
                          <option value="docker">Docker</option>
                          <option value="docker-compose">Docker Compose</option>
                          <option value="kubernetes">Kubernetes</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          🔑 Agent API Key
                        </label>
                        <input
                          type="password"
                          value={deploymentState.apiKey}
                          onChange={(e) => {
                            const newApiKey = e.target.value
                            onDeploymentStateChange(agent.id!, { 
                              apiKey: newApiKey 
                            })
                            // Save to localStorage
                            saveApiKey(agent.id!, newApiKey)
                          }}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm transition-colors"
                          placeholder="Enter your API key..."
                        />
                      </div>
                    </div>

                    {/* Configuration Display */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                          {deploymentState.type === 'docker' && <>🚀 Docker Command</>}
                          {deploymentState.type === 'docker-compose' && <>🐳 Docker Compose</>}
                          {deploymentState.type === 'kubernetes' && <>☸️ Kubernetes</>}
                        </h4>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const config = deploymentState.type === 'docker-compose' 
                                ? generateDockerComposeConfig(agent, deploymentState.apiKey)
                                : deploymentState.type === 'kubernetes' 
                                ? generateKubernetesConfig(agent, deploymentState.apiKey)
                                : generateDockerCommand(agent, deploymentState.apiKey)
                              await navigator.clipboard.writeText(config)
                              toast.success('Configuration copied to clipboard')
                            } catch (error) {
                              console.error('Failed to copy config:', error)
                              toast.error('Failed to copy configuration')
                            }
                          }}
                          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium shadow-sm"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                          <span>Copy</span>
                        </button>
                      </div>
                      
                      <div className="bg-gray-900 rounded-lg overflow-hidden">
                        <pre className="p-6 text-green-400 text-sm font-mono overflow-x-auto">
                          {deploymentState.type === 'docker-compose' && generateDockerComposeConfig(agent, deploymentState.apiKey)}
                          {deploymentState.type === 'kubernetes' && generateKubernetesConfig(agent, deploymentState.apiKey)}
                          {deploymentState.type === 'docker' && generateDockerCommand(agent, deploymentState.apiKey)}
                        </pre>
                      </div>
                    </div>

                    {/* Instructions */}
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                      <h4 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-3 flex items-center">
                        📋 Deployment Steps
                      </h4>
                      {deploymentState.type === 'docker-compose' && (
                        <ol className="text-sm text-green-700 dark:text-green-300 space-y-1 list-decimal list-inside">
                          <li>Save configuration as <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">docker-compose.yml</code></li>
                          <li>Run <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">docker compose up -d</code></li>
                          <li>Monitor with <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">docker compose logs mazpin-agent</code></li>
                          <li>To upgrade: <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">docker compose pull && docker compose up -d</code></li>
                          <li>To uninstall: <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">docker compose down</code></li>
                        </ol>
                      )}
                      {deploymentState.type === 'kubernetes' && (
                        <ol className="text-sm text-green-700 dark:text-green-300 space-y-1 list-decimal list-inside">
                          <li>Save configuration as <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">mazpin-agent.yaml</code></li>
                          <li>Apply with <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">kubectl apply -f mazpin-agent.yaml</code></li>
                          <li>Monitor with <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">kubectl logs deployment/mazpin-agent</code></li>
                          <li>To uninstall: <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">kubectl delete -f mazpin-agent.yaml</code></li>
                        </ol>
                      )}
                      {deploymentState.type === 'docker' && (
                        <ol className="text-sm text-green-700 dark:text-green-300 space-y-1 list-decimal list-inside">
                          <li>Copy and run the command in your terminal</li>
                          <li>Monitor with <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">docker logs mazpin-agent</code></li>
                          <li>To upgrade: <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">docker pull mazpin/agent:{agentVersion} && docker stop mazpin-agent && docker rm mazpin-agent</code> then run the command again</li>
                          <li>To uninstall: <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs">docker stop mazpin-agent && docker rm mazpin-agent</code></li>
                        </ol>
                      )}
                    </div>

                    {/* Technical Info */}
                    <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-center">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Version</div>
                        <div className="text-sm font-mono text-gray-900 dark:text-white">v{agentVersion}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          {deploymentState.type === 'kubernetes' ? 'Networking' : 'Network'}
                        </div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          {deploymentState.type === 'kubernetes' ? 'Pod network' : 'Host network'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Policy</div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          {deploymentState.type === 'kubernetes' ? 'Always restart' : 'Unless stopped'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if agent data or deployment state changes
  return (
    JSON.stringify(prevProps.agent) === JSON.stringify(nextProps.agent) &&
    JSON.stringify(prevProps.deploymentState) === JSON.stringify(nextProps.deploymentState) &&
    prevProps.agentVersion === nextProps.agentVersion &&
    prevProps.currentUser?.id === nextProps.currentUser?.id
  )
})

AgentCard.displayName = 'AgentCard'

export function AgentsPage() {
  const { user: currentUser } = useAuthStore()
  
  const {
    agents,
    pagination,
    loading,
    error,
    fetchAgents,
    createAgent,
    updateAgent,
    deleteAgent,
    getAgentEncryption,
    saveAgentEncryption,
    refreshAgents,
    clearError,
  } = useAgents()

  const {
    invitations: sharingInvitations,
    sharedAgents: sharingSharedAgents,
    fetchInvitations,
    fetchSharedAgents
  } = useAgentSharing()
  
  // Persist Agents page state across navigation
  const {
    allAgents,
    setAllAgents,
    currentPage,
    setCurrentPage,
    filters,
    setFilters,
    activeTab,
    setActiveTab,
    pagination: storedPagination,
    setPagination: setStoredPagination,
    scrollTop,
    setScrollTop,
    invitations: storedInvitations,
    setInvitations: setStoredInvitations,
    sharedAgents: storedSharedAgents,
    setSharedAgents: setStoredSharedAgents,
  } = useAgentsPageStore()
  
  // Invitations state for counting and displaying, persisted in store
  const [invitations, setInvitations] = useState<typeof sharingInvitations>(storedInvitations || [])
  // Shared agents state for counting and displaying, persisted in store
  const [sharedAgents, setSharedAgents] = useState<typeof sharingSharedAgents>(storedSharedAgents || [])
  
  const handleInvitationsUpdate = useCallback((newInvitations: typeof sharingInvitations) => {
    setInvitations(newInvitations)
    setStoredInvitations(newInvitations)
  }, [setStoredInvitations])

  const handleSharedAgentsUpdate = useCallback((newSharedAgents: typeof sharingSharedAgents) => {
    setSharedAgents(newSharedAgents)
    setStoredSharedAgents(newSharedAgents)
  }, [setStoredSharedAgents])


  const [showForm, setShowForm] = useState(false)
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null)
  const [editingPermission, setEditingPermission] = useState<'owner' | 'write' | 'read'>('owner')
  const [formData, setFormData] = useState<AgentFormData>({
    name: '',
    description: '',
    is_active: true,
    capabilities: {},
    metadata: {},
    encryption: {
      enabled: true,
      key: ''
    },
  })
  
  const [openaiSettings, setOpenaiSettings] = useState<AgentOpenAISettings>({
    enabled: false,
    apiKey: ''
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [showValidationError, setShowValidationError] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  const [newApiKey, setNewApiKey] = useState('')
  const [showEncryptionTooltip, setShowEncryptionTooltip] = useState(false)
  
  // Tab state moved to store
  
  // Sharing modals
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)
  const [agentToShare, setAgentToShare] = useState<Agent | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [pausePeriodicRefresh, setPausePeriodicRefresh] = useState(false)
  
  // Agent-specific deployment states
  const [agentDeploymentStates, setAgentDeploymentStates] = useState<Record<string, {
    type: 'docker' | 'docker-compose' | 'kubernetes'
    showConfig: boolean
    apiKey: string
  }>>({})
  
  const getAgentDeploymentState = (agentId: string) => {
    return agentDeploymentStates[agentId] || { 
      type: 'docker', 
      showConfig: false, 
      apiKey: ''
    }
  }
  
  const setAgentDeploymentState = (agentId: string, updates: Partial<{ 
    type: 'docker' | 'docker-compose' | 'kubernetes'
    showConfig: boolean
    apiKey: string
  }>) => {
    setAgentDeploymentStates(prev => ({
      ...prev,
      [agentId]: { ...getAgentDeploymentState(agentId), ...updates }
    }))
  }

  // Determine user's permission level for an agent
  const getUserPermissionForAgent = useCallback((agent: Agent): 'owner' | 'write' | 'read' => {
    if (!currentUser) return 'read'
    
    // If the current user created the agent, they are the owner
    if (agent.created_by?.id === currentUser.id) {
      return 'owner'
    }
    
    // Check if this agent is in the shared agents list
    const sharedAgent = sharedAgents.find(sa => sa.id === agent.id)
    if (sharedAgent) {
      // Return the actual permission from the shared agent
      return sharedAgent.permission || 'read'
    }
    
    // If the agent is in "My Agents" but user didn't create it and it's not in shared list,
    // they might have become the owner through ownership transfer
    // Default to 'write' as a safe assumption (could be ownership transfer case)
    return 'write'
  }, [currentUser, sharedAgents])

  // Convert API base URL to WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081'
    // Convert http://domain or https://domain to wss://domain:443
    const wssBase = apiBaseUrl
      .replace(/^https?:\/\//, 'wss://')
      .replace(/:\d+$/, '')
    return `${wssBase}:443`
  }, [])

  // Get agent version from environment variable
  const agentVersion: string = (import.meta as unknown as { env?: Record<string, unknown> }).env?.VITE_AGENT_VERSION as string || '0.0.19'
  
  // Infinite scroll state
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isFetchingMore, setIsFetchingMore] = useState(false)

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null)

  // Fetch next page for infinite scroll
  const fetchNextPage = useCallback(async () => {
    if (isFetchingMore || !pagination?.has_next) return
    
    setIsFetchingMore(true)
    const nextPage = currentPage + 1
    
    try {
      await fetchAgents(nextPage, 20, filters)
      setCurrentPage(nextPage)
    } finally {
      setIsFetchingMore(false)
    }
  }, [currentPage, pagination?.has_next, isFetchingMore, fetchAgents, filters, setCurrentPage])

  // Update allAgents when agents change
  React.useEffect(() => {
    // Avoid wiping stored list with an empty hook state on remount/loading
    if (currentPage === 1) {
      if (agents.length === 0) {
        // If a completed fetch returned truly empty results, reflect that
        if (!loading && pagination && (pagination.total_count === 0 || agents.length === 0)) {
          if (allAgents.length !== 0) setAllAgents([])
        }
        return
      }
      // For page 1, always update with fresh data to ensure status changes are reflected
      setAllAgents(agents)
    } else {
      if (agents.length === 0) return
      // Update existing agents and add new ones
      setAllAgents((prev: Agent[]) => {
        const agentsMap = new Map(agents.map(agent => [agent.id, agent]))
        
        // Update existing agents or keep them if not in the current batch
        const updatedAgents = prev.map((existingAgent: Agent) => {
          const updatedAgent = agentsMap.get(existingAgent.id)
          if (updatedAgent) {
            agentsMap.delete(existingAgent.id) // Remove from map as it's been processed
            return updatedAgent // Use the updated agent data
          }
          return existingAgent // Keep the existing agent if not in current batch
        })
        
        // Add any new agents that weren't in the previous list
        const newAgents = Array.from(agentsMap.values())
        
        return [...updatedAgents, ...newAgents]
      })
    }
  }, [agents, currentPage, setAllAgents, loading, pagination, allAgents])

  // Keep pagination in store to preserve across navigation
  useEffect(() => {
    if (pagination) {
      setStoredPagination(pagination)
    }
  }, [pagination, setStoredPagination])

  // Search and filter handling
  const debouncedSearch = useDebounce((...args: unknown[]) => {
    const [firstArg] = args
    const search = typeof firstArg === 'string' ? firstArg : ''
    const newFilters = { ...filters, search: search || undefined }
    setFilters(newFilters)
    setCurrentPage(1) // Reset to first page on search
    void fetchAgents(1, 20, newFilters)
  }, 300)

  const handleSearch = (search: string) => {
    debouncedSearch(search)
  }

  const handleStatusFilter = (status: 'active' | 'inactive' | undefined) => {
    const newFilters = { ...filters, status }
    setFilters(newFilters)
    setCurrentPage(1) // Reset to first page on filter change
    void fetchAgents(1, 20, newFilters)
  }

  const handleConnectionStatusFilter = (connectionStatus: 'connected' | 'disconnected' | 'error' | undefined) => {
    const newFilters = { ...filters, connectionStatus }
    setFilters(newFilters)
    setCurrentPage(1) // Reset to first page on filter change
    void fetchAgents(1, 20, newFilters)
  }

  // Set up infinite scroll on the list container and restore/save scrollTop
  useEffect(() => {
    let scrollTimeout: number

    const handleScroll = () => {
      // Debounce scroll events to prevent excessive calls
      clearTimeout(scrollTimeout)
      scrollTimeout = window.setTimeout(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const { scrollTop: st, scrollHeight, clientHeight } = container
        const isNearBottom = st + clientHeight >= scrollHeight - 300

        console.log('Scroll check:', {
          scrollTop: st,
          clientHeight,
          scrollHeight,
          isNearBottom,
          hasNext: pagination?.has_next,
          isFetching: isFetchingMore
        })

        if (isNearBottom && pagination?.has_next && !isFetchingMore) {
          console.log('Loading more agents...')
          void fetchNextPage()
        }
        // Save scrollTop continuously to keep position on navigation
        setScrollTop(st)
      }, 100) // 100ms debounce
    }

    const container = scrollContainerRef.current
    if (container) {
      // Restore previous scroll position
      if (scrollTop > 0) {
        container.scrollTop = scrollTop
      }
      container.addEventListener('scroll', handleScroll, { passive: true })
      return () => {
        // Save scroll position on unmount
        setScrollTop(container.scrollTop)
        container.removeEventListener('scroll', handleScroll)
        clearTimeout(scrollTimeout)
      }
    }
    return undefined
  }, [pagination?.has_next, isFetchingMore, fetchNextPage, scrollTop, setScrollTop])


  // Generate a new encryption key
  const handleGenerateKey = () => {
    const newKey = generateEncryptionKey()
    setFormData(prev => ({
      ...prev,
      encryption: {
        ...prev.encryption!,
        key: newKey
      }
    }))
  }

  // Copy encryption key to clipboard
  const handleCopyKey = async () => {
    if (formData.encryption?.key) {
      try {
        await navigator.clipboard.writeText(formData.encryption.key)
        toast.success('Encryption key copied to clipboard')
      } catch (error) {
        console.error('Failed to copy key:', error)
        toast.error('Failed to copy encryption key to clipboard')
      }
    }
  }

  // Form validation
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      errors.name = 'Agent name is required'
    }
    
    if (!formData.encryption?.key?.trim()) {
      errors.encryptionKey = 'Agent Encryption Key is required'
    }
    
    if (formData.encryption?.key && !isValidEncryptionKey(formData.encryption.key)) {
      errors.encryptionKey = 'Invalid encryption key format'
    }
    
    // Validate OpenAI settings if enabled
    if (openaiSettings.enabled) {
      if (!openaiSettings.apiKey.trim()) {
        errors.openaiApiKey = 'OpenAI API key is required when AI Assistant is enabled'
      } else if (!openaiSettings.apiKey.startsWith('sk-')) {
        errors.openaiApiKey = 'OpenAI API key should start with "sk-"'
      }
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleAddAgent = () => {
    setEditingAgent(null)
    setFormData({
      name: '',
      description: '',
      is_active: true,
      capabilities: {},
      metadata: {},
      encryption: {
        enabled: true,
        key: ''
      },
    })
    setOpenaiSettings({
      enabled: false,
      apiKey: ''
    })
    setFormErrors({})
    setShowValidationError(false)
    setNewApiKey('')
    setShowForm(true)
  }

  const handleEditAgent = (agent: Agent, permission?: 'owner' | 'write' | 'read') => {
    setEditingAgent(agent)
    const userPermission = permission || getUserPermissionForAgent(agent)
    setEditingPermission(userPermission)
    
    const encryptionSettings = agent.id ? getAgentEncryption(agent.id) : { enabled: true, key: '' }
    encryptionSettings.enabled = true // Always enable encryption
    
    const existingOpenAISettings = agent.id ? getAgentOpenAISettings(agent.id) : { enabled: false, apiKey: '' }
    
    setFormData({
      name: agent.name || '',
      description: agent.description || '',
      is_active: agent.is_active || false,
      capabilities: agent.capabilities || {},
      metadata: agent.metadata || {},
      encryption: encryptionSettings,
    })
    setOpenaiSettings(existingOpenAISettings)
    setFormErrors({})
    setShowValidationError(false)
    setNewApiKey('')
    setShowForm(true)
  }

  const handleSaveAgent = async () => {
    // For read-only users, only save encryption and OpenAI settings locally
    if (editingPermission === 'read' && editingAgent?.id) {
      setSubmitting(true)
      
      try {
        // Save encryption settings to localStorage
        if (formData.encryption) {
          saveAgentEncryption(editingAgent.id, formData.encryption)
        }
        
        // Save OpenAI settings to localStorage
        saveAgentOpenAISettings(editingAgent.id, openaiSettings)
        
        toast.success('Encryption and API keys have been saved locally.')
        setShowForm(false)
        setEditingAgent(null)
        setEditingPermission('owner')
      } catch (error) {
        console.error('Failed to save settings:', error)
        toast.error('Failed to save settings locally.')
      } finally {
        setSubmitting(false)
      }
      return
    }
    
    // For users with write/owner permissions, proceed with normal update
    if (!validateForm()) {
      setShowValidationError(true)
      return
    }
    
    setShowValidationError(false)
    
    setSubmitting(true)
    clearError()
    
    try {
      if (editingAgent) {
        // Update existing agent
        const success = await updateAgent(editingAgent.id!, formData)
        
        if (success) {
          // Save OpenAI settings
          if (editingAgent.id) {
            try {
              saveAgentOpenAISettings(editingAgent.id, openaiSettings)
            } catch (settingsError) {
              console.warn('Failed to save OpenAI settings:', settingsError)
              toast('Agent updated but OpenAI settings may not have been saved.', { icon: '⚠️' })
            }
          }
          
          toast.success(`Agent "${formData.name}" has been updated successfully.`)
          setShowForm(false)
          setEditingAgent(null)
          setEditingPermission('owner')
        } else {
          toast.error(error || 'Failed to update agent. Please try again.')
        }
      } else {
        // Create new agent
        const result = await createAgent(formData)
        
        if (result) {
          // Save OpenAI settings for the new agent
          if (result.agent?.id) {
            try {
              saveAgentOpenAISettings(result.agent.id, openaiSettings)
            } catch (settingsError) {
              console.warn('Failed to save OpenAI settings:', settingsError)
              toast('Agent created but OpenAI settings may not have been saved.', { icon: '⚠️' })
            }
          }
          
          toast.success(`Agent "${formData.name}" has been created successfully.`)
          setNewApiKey(result.apiKey)
          // Save the API key for the newly created agent
          if (result.agent?.id && result.apiKey) {
            saveAgentApiKey(result.agent.id, result.apiKey)
          }
          setShowApiKey(true)
          setShowForm(false)
          setEditingAgent(null)
        } else {
          toast.error(error || 'Failed to create agent. Please try again.')
        }
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteAgent = (agentId: string) => {
    const agent = allAgents.find(a => a.id === agentId)
    if (!agent) return
    
    setAgentToDelete(agent)
    setShowDeleteModal(true)
  }

  const confirmDeleteAgent = useCallback(async () => {
    if (!agentToDelete) return
    
    const success = await deleteAgent(agentToDelete.id!)
    if (success) {
      toast.success(`Agent "${agentToDelete.name}" has been deleted successfully.`)
    } else {
      toast.error(error || 'Failed to delete agent. Please try again.')
    }
    
    setAgentToDelete(null)
    setShowDeleteModal(false)
  }, [agentToDelete, deleteAgent, error])

  const handleToggleAgent = async (agentId: string) => {
    const agent = allAgents.find(a => a.id === agentId)
    if (!agent) return
    
    const success = await updateAgent(agentId, {
      is_active: !agent.is_active
    })
    
    if (success) {
      // Update the allAgents state to reflect the change
      setAllAgents(allAgents.map(a => 
        a.id === agentId ? { ...a, is_active: !agent.is_active } : a
      ))
      
      const action = !agent.is_active ? 'enabled' : 'disabled'
      toast.success(`Agent "${agent.name}" has been ${action}.`)
    } else {
      toast.error(error || 'Failed to toggle agent status. Please try again.')
    }
  }

  const handleRefresh = () => {
    setCurrentPage(1)
    if (activeTab === 'my-agents') {
      void refreshAgents(1, 20, filters)
    } else {
      // Trigger refresh in child components (invitations/shared agents)
      setRefreshTrigger(prev => prev + 1)
    }
  }

  // Permissions handler
  const handleViewPermissions = (agent: Agent) => {
    setAgentToShare(agent)
    setShowPermissionsModal(true)
  }

  // Function to refresh sharing data (invitations and shared agents)
  const refreshSharingData = useCallback(async (options: { isBackground?: boolean, countsOnly?: boolean } = {}) => {
    const { isBackground = false, countsOnly = false } = options
    
    try {
      // Fetch both invitations and shared agents data
      const [invitationsData, sharedAgentsData] = await Promise.all([
        fetchInvitations(),
        fetchSharedAgents()
      ])
      
      // If countsOnly, always update the data for badge counts
      if (countsOnly) {
        setInvitations(invitationsData)
        setStoredInvitations(invitationsData)
        setSharedAgents(sharedAgentsData)
        setStoredSharedAgents(sharedAgentsData)
      } else {
        // Always update invitations for badge counts (they're lightweight)
        setInvitations(invitationsData)
        setStoredInvitations(invitationsData)
        
        // Update shared agents based on active tab for content refresh
        if (!isBackground || activeTab === 'shared-agents') {
          setSharedAgents(sharedAgentsData)
          setStoredSharedAgents(sharedAgentsData)
        }
      }
    } catch (error) {
      console.error('Failed to refresh sharing data:', error)
    }
  }, [fetchInvitations, fetchSharedAgents, activeTab, setStoredInvitations, setStoredSharedAgents])

  const handleSharingChange = useCallback((newSharedAgentsCount?: number) => {
    // Pause periodic refresh temporarily to avoid race conditions
    setPausePeriodicRefresh(true)
    
    // If we have an immediate count update, use it
    if (typeof newSharedAgentsCount === 'number') {
      // Update the shared agents count immediately for UI responsiveness
      setSharedAgents((prev) => {
        // Keep the same agents but update the length for display
        const newList = prev.slice(0, newSharedAgentsCount)
        setStoredSharedAgents(newList) // Update store with new list
        return newList
      })
    }
    
    // Trigger refresh in child components
    setRefreshTrigger(prev => prev + 1)
    // Also refresh My Agents list when sharing changes (e.g., invitation accepted)
    void refreshAgents(1, 20, filters)
    setCurrentPage(1)
    
    // Add a delay before fetching to allow backend to process the change
    setTimeout(() => {
      try {
        void refreshSharingData({ isBackground: false })
      } catch (error) {
        console.error('Failed to refresh sharing data:', error)
      } finally {
        // Resume periodic refresh after data is updated
        setTimeout(() => {
          setPausePeriodicRefresh(false)
        }, 1000) // Give an extra second before resuming periodic refresh
      }
    }, 500) // 500ms delay to ensure backend has processed the change
  }, [refreshAgents, filters, refreshSharingData, setCurrentPage, setStoredSharedAgents])

  const getPendingInvitationsCount = () => {
    return invitations.filter(inv => inv.status === 'pending').length
  }

  // Track if initial fetch has been done to prevent duplicates
  const hasFetchedRef = useRef(false)
  
  // Initialize agents and invitations data on mount - preserve store state when available
  useEffect(() => {
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true

    if (allAgents.length > 0) {
      // If we already have list in store, just refresh in background
      void refreshSharingData({ countsOnly: true })
      void refreshAgents(currentPage, 20, filters)
      return
    }

    void refreshAgents(1, 20, filters)
    // Initialize invitations from store if present; otherwise fetch
    if (!storedInvitations || storedInvitations.length === 0) {
      void refreshSharingData({ countsOnly: true })
    } else {
      setInvitations(storedInvitations)
    }
  }, [storedInvitations, allAgents.length, currentPage, refreshSharingData, filters, refreshAgents])

  // Reset to first page when switching to my-agents tab
  useEffect(() => {
    if (activeTab === 'my-agents' && !showForm) {
      setCurrentPage(1) // Reset to first page when switching tabs
    }
  }, [activeTab, showForm, setCurrentPage])

  // Unified auto-refresh logic - refresh appropriate data based on active tab
  useEffect(() => {
    if (showForm) {
      return // Don't auto-refresh when in form mode
    }

    let intervalId: NodeJS.Timeout | undefined

    // Start periodic refresh after initial delay to avoid conflicts with mount fetch
    const timeoutId = setTimeout(() => {
      intervalId = setInterval(async () => {
        // Skip refresh if we're pausing for a local update
        if (pausePeriodicRefresh) {
          return
        }
        
        // Always fetch sharing data for badge counts (but don't affect current tab's content)
        if (activeTab === 'my-agents') {
          // Refresh all currently loaded agents to ensure status updates are visible
          // Calculate how many agents are currently loaded (current page * items per page)
          const totalLoadedAgents = currentPage * 20
          void refreshAgents(1, totalLoadedAgents, filters)
          // Also fetch sharing data just for counts
          await refreshSharingData({ countsOnly: true })
        } else if (activeTab === 'invitations' || activeTab === 'shared-agents') {
          // Fetch fresh data from server and update local state
          // Invitations will always be updated for badge counts
          await refreshSharingData({ isBackground: true })
          // Also trigger refresh in child components for any local updates
          setRefreshTrigger(prev => prev + 1)
        }
      }, 2000) // Refresh every 2 seconds
    }, 2000) // Wait 2 seconds before starting periodic refresh

    return () => {
      clearTimeout(timeoutId)
      if (intervalId) clearInterval(intervalId)
    }
  }, [showForm, activeTab, currentPage, filters, refreshAgents, pausePeriodicRefresh, refreshSharingData])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getActiveAgentsCount = () => {
    return allAgents.filter(agent => agent.is_active).length
  }

  const getConnectedAgentsCount = () => {
    return allAgents.filter(agent => agent.connection_status === 'connected').length
  }

  const formatCountWithPlus = (count: number, hasMore: boolean) => {
    return hasMore ? `${count}+` : count.toString()
  }

  // useCallback for functions passed as JSX props
  const handleDeleteModalClose = useCallback(() => {
    setShowDeleteModal(false)
    setAgentToDelete(null)
  }, [])

  const handleDeleteModalConfirm = useCallback(() => {
    void confirmDeleteAgent()
  }, [confirmDeleteAgent])

  const handleEncryptionTooltipEnter = useCallback(() => {
    setShowEncryptionTooltip(true)
  }, [])

  const handleEncryptionTooltipLeave = useCallback(() => {
    setShowEncryptionTooltip(false)
  }, [])

  // Generate deployment configurations for specific agent
  const generateDockerComposeConfig = useCallback((agent: Agent, apiKey: string) => {
    const agentId = agent.id || 'AGENT_ID'
    const encryptionSettings = agent.id ? getAgentEncryption(agent.id) : { enabled: true, key: 'ENCRYPTION_KEY' }
    const encryptionKey = encryptionSettings.key || 'ENCRYPTION_KEY'
    const websocketUrl = getWebSocketUrl()
    
    return `services:
  mazpin-agent:
    container_name: mazpin-agent
    image: mazpin/agent:${agentVersion}
    restart: unless-stopped
    network_mode: host
    environment:
      AGENT_WEBSOCKET_URL: "${websocketUrl}"
      AGENT_ID: "${agentId}"
      AGENT_API_KEY: "${apiKey || 'AGENT_API_KEY'}"
      ENCRYPTION_KEY: "${encryptionKey}"`
  }, [getAgentEncryption, getWebSocketUrl, agentVersion])

  const generateKubernetesConfig = useCallback((agent: Agent, apiKey: string) => {
    const agentId = agent.id || 'AGENT_ID'
    const encryptionSettings = agent.id ? getAgentEncryption(agent.id) : { enabled: true, key: 'ENCRYPTION_KEY' }
    const encryptionKey = encryptionSettings.key || 'ENCRYPTION_KEY'
    const websocketUrl = getWebSocketUrl()
    
    return `apiVersion: v1
kind: Secret
metadata:
  name: mazpin-agent-secrets
  labels:
    app: mazpin-agent
type: Opaque
stringData:
  AGENT_WEBSOCKET_URL: "${websocketUrl}"
  AGENT_ID: "${agentId}"
  AGENT_API_KEY: "${apiKey || 'AGENT_API_KEY'}"
  ENCRYPTION_KEY: "${encryptionKey}"

---

apiVersion: apps/v1
kind: Deployment
metadata:
  name: mazpin-agent
  labels:
    app: mazpin-agent
spec:
  replicas: 1
  strategy:
    rollingUpdate:
      maxSurge: 100%
      maxUnavailable: 100%
  selector:
    matchLabels:
      app: mazpin-agent
  template:
    metadata:
      deletionGracePeriodSeconds: 0
      labels:
        app: mazpin-agent
    spec:
      containers:
      - name: mazpin-agent
        image: mazpin/agent:${agentVersion}
        imagePullPolicy: IfNotPresent
        resources:
          requests:
            memory: "256Mi"
            cpu: "100m"
          limits:
            memory: "2048Mi"
            cpu: "2000m"
        livenessProbe:
          exec:
            command:
            - pgrep
            - -f
            - mazpin-agent
          initialDelaySeconds: 30
          periodSeconds: 30
          timeoutSeconds: 10
          failureThreshold: 3
        readinessProbe:
          exec:
            command:
            - pgrep
            - -f
            - mazpin-agent
          initialDelaySeconds: 5
          periodSeconds: 10
          timeoutSeconds: 5
          failureThreshold: 3
        envFrom:
        - secretRef:
            name: mazpin-agent-secrets
      restartPolicy: Always`
  }, [getAgentEncryption, getWebSocketUrl, agentVersion])

  const generateDockerCommand = useCallback((agent: Agent, apiKey: string) => {
    const agentId = agent.id || 'AGENT_ID'
    const encryptionSettings = agent.id ? getAgentEncryption(agent.id) : { enabled: true, key: 'ENCRYPTION_KEY' }
    const encryptionKey = encryptionSettings.key || 'ENCRYPTION_KEY'
    const websocketUrl = getWebSocketUrl()
    
    return `docker run -d \\
  --name mazpin-agent \\
  --restart unless-stopped \\
  --network host \\
  -e AGENT_WEBSOCKET_URL="${websocketUrl}" \\
  -e AGENT_ID="${agentId}" \\
  -e AGENT_API_KEY="${apiKey || 'AGENT_API_KEY'}" \\
  -e ENCRYPTION_KEY="${encryptionKey}" \\
  mazpin/agent:${agentVersion}`
  }, [getAgentEncryption, getWebSocketUrl, agentVersion])

  return (
    <>
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteModalConfirm}
        title="Delete Agent"
        message={`Are you sure you want to delete agent "${agentToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />

      <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CpuChipIcon className="w-6 h-6 text-primary-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {showForm ? (editingAgent ? (editingPermission === 'read' ? 'Configure Agent Keys' : 'Edit Agent') : 'Add New Agent') : 'Agents Management'}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {showForm 
                  ? (editingAgent ? (editingPermission === 'read' ? `Configuring encryption and API keys for ${editingAgent.name}` : `Editing ${editingAgent.name}`) : 'Create a new database agent')
                  : 'Configure, monitor, and share your database agents'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {!showForm ? (
              <>
                <button type="button"
                  onClick={handleRefresh}
                  disabled={loading}
                  className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  <ArrowPathIcon className={clsx('w-4 h-4', loading && 'animate-spin')} />
                  <span>Refresh</span>
                </button>
                <button type="button"
                  onClick={handleAddAgent}
                  className="flex items-center space-x-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>Add Agent</span>
                </button>
              </>
            ) : (
              <button type="button"
                onClick={() => {
                  setShowForm(false)
                  setEditingAgent(null)
                  setEditingPermission('owner')
                }}
                className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back to List</span>
              </button>
            )}
          </div>
        </div>

        {/* Stats - Only show when not in form mode */}
        {!showForm && (
          <div className="flex items-center space-x-4 mt-3">
            <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg px-4 py-2">
              <ServerIcon className="w-5 h-5 text-blue-500 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Agents:</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white ml-1.5">
                {storedPagination?.total_count || pagination?.total_count || formatCountWithPlus(allAgents.length, (storedPagination?.has_next || pagination?.has_next || false))}
              </span>
            </div>
            <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg px-4 py-2">
              <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Active:</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white ml-1.5">
                {formatCountWithPlus(getActiveAgentsCount(), (storedPagination?.has_next || pagination?.has_next || false))}
              </span>
            </div>
            <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg px-4 py-2">
              <WifiIcon className="w-5 h-5 text-primary-500 mr-2" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Connected:</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white ml-1.5">{getConnectedAgentsCount()}</span>
            </div>
          </div>
        )}

        {/* Filters - Only show when not in form mode */}
        {!showForm && (
          <div className="flex flex-col sm:flex-row gap-3 mt-3">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search agents by name or description..."
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  onChange={(e) => { handleSearch(e.target.value) }}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                onChange={(e) => {
                  const v = e.target.value
                  if (v === 'active' || v === 'inactive') {
                    handleStatusFilter(v)
                  } else {
                    handleStatusFilter(undefined)
                  }
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <select
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                onChange={(e) => {
                  const v = e.target.value
                  if (v === 'connected' || v === 'disconnected' || v === 'error') {
                    handleConnectionStatusFilter(v)
                  } else {
                    handleConnectionStatusFilter(undefined)
                  }
                }}
              >
                <option value="">All Connections</option>
                <option value="connected">Connected</option>
                <option value="disconnected">Disconnected</option>
                <option value="error">Error</option>
              </select>
            </div>
          </div>
        )}
        
        {/* Tabs - Only show when not in form mode */}
        {!showForm && (
          <div className="flex border-t border-gray-200 dark:border-gray-700 mt-4">
            <button
              type="button"
              onClick={() => { setActiveTab('my-agents') }}
              className={clsx(
                'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'my-agents'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              )}
            >
              <div className="flex items-center justify-center space-x-2">
                <CpuChipIcon className="w-4 h-4" />
                <span>My Agents</span>
                <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded-full">
                  {allAgents.length}
                </span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('shared-agents') }}
              className={clsx(
                'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'shared-agents'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              )}
            >
              <div className="flex items-center justify-center space-x-2">
                <ShareIcon className="w-4 h-4" />
                <span>Shared with Me</span>
                <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded-full">
                  {sharedAgents.length}
                </span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('invitations') }}
              className={clsx(
                'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors relative',
                activeTab === 'invitations'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              )}
            >
              <div className="flex items-center justify-center space-x-2">
                <div className="relative">
                  <BellIcon className="w-4 h-4" />
                  {getPendingInvitationsCount() > 0 && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </div>
                <span>Invitations</span>
                <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded-full">
                  {getPendingInvitationsCount()}
                </span>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 mx-6 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <XCircleIcon className="w-5 h-5 text-red-400 mr-2" />
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
            <button type="button"
              onClick={clearError}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <XCircleIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* API Key Display Modal */}
      {showApiKey && newApiKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <KeyIcon className="w-6 h-6 text-green-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Agent Created Successfully</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Your agent has been created. Please save this API key as it will only be shown once:
              </p>
              <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg mb-4">
                <code className="text-sm font-mono text-gray-900 dark:text-white break-all">
                  {newApiKey}
                </code>
              </div>
              <div className="flex justify-end space-x-3">
                <button type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(newApiKey)
                      toast.success('API key copied to clipboard')
                    } catch (error) {
                      console.error('Failed to copy API key:', error)
                      toast.error('Failed to copy API key to clipboard')
                    }
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                  <span>Copy</span>
                </button>
                <button type="button"
                  onClick={() => {
                    setShowApiKey(false)
                    setNewApiKey('')
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  I've Saved It
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {showForm ? (
          /* Agent Form */
          <div className="h-full overflow-y-auto p-6">
            <div className="max-w-2xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="space-y-6">
                {/* Read-only permission notice */}
                {editingPermission === 'read' && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start">
                      <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                          Limited Editing Access
                        </p>
                        <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                          You have read-only access to this agent. You can only modify the encryption key and OpenAI API key settings.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Agent Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value })
                      setShowValidationError(false)
                    }}
                    disabled={editingPermission === 'read'}
                    className={clsx(
                      'w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                      editingPermission === 'read' && 'opacity-50 cursor-not-allowed',
                      formErrors.name ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    )}
                    placeholder="Enter agent name..."
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.name}</p>
                  )}
                </div>

                {/* Encryption Settings */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center mb-4">
                    <ShieldCheckIcon className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-2" />
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">End-to-End Encryption</h4>
                    <div className="relative ml-2">
                      <button
                        type="button"
                        onMouseEnter={handleEncryptionTooltipEnter}
                        onMouseLeave={handleEncryptionTooltipLeave}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <InformationCircleIcon className="h-4 w-4" />
                      </button>
                      {showEncryptionTooltip && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-80 p-3 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg z-50">
                          <div className="space-y-2">
                            <p className="font-medium">🔐 AES-256 End-to-End Encryption</p>
                            <p>Protects all sensitive data between your browser and the agent. Mazpin servers cannot decrypt or access your data.</p>
                            <p className="text-yellow-300">⚠️ You must configure the same encryption key in your agent for this to work.</p>
                          </div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Description */}
                    <div className="text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="font-medium text-blue-800 dark:text-blue-200 mb-1">🛡️ Zero-Knowledge Architecture</p>
                      <p className="text-blue-700 dark:text-blue-300 text-xs leading-relaxed">
                        Enable AES-256 encryption to secure all communication between your browser and agent.
                        This ensures complete privacy - Mazpin servers cannot access your database queries, results, or any sensitive information. 
                        Your data remains encrypted in transit and only you control the decryption keys.
                      </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <KeyIcon className="h-4 w-4 inline mr-1" />
                          Agent Encryption Key <span className="text-red-500">*</span>
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={formData.encryption?.key || ''}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                encryption: {
                                  ...prev.encryption!,
                                  key: e.target.value
                                }
                              }))
                              setShowValidationError(false)
                            }}
                            className={clsx(
                              'flex-1 px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm',
                              formErrors.encryptionKey ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                            )}
                            placeholder="Enter 32-byte base64 encryption key..."
                          />
                          <button
                            type="button"
                            onClick={handleGenerateKey}
                            className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                            title="Generate secure random key"
                          >
                            Generate
                          </button>
                          {formData.encryption?.key && (
                            <button
                              type="button"
                              onClick={() => void handleCopyKey()}
                              className="px-3 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors text-sm"
                              title="Copy key to clipboard"
                            >
                              <DocumentDuplicateIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        {formErrors.encryptionKey && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.encryptionKey}</p>
                        )}
                        
                        {/* Important Instructions */}
                        <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                            📋 Required Setup Steps:
                          </p>
                          <ol className="text-xs text-amber-700 dark:text-amber-300 space-y-1 list-decimal list-inside">
                            <li><strong>Copy this encryption key</strong> (use the copy button above)</li>
                            <li><strong>Configure your agent</strong> with the same encryption key</li>
                            <li><strong>Enable encryption</strong> in your agent's configuration</li>
                            <li><strong>Restart your agent</strong> to apply the encryption settings</li>
                          </ol>
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                            ⚠️ <strong>Important:</strong> Keep this key secure and never share it. If lost, you'll need to generate a new key and reconfigure your agent.
                          </p>
                        </div>

                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                          <p>🔒 <strong>Storage:</strong> Key is stored locally in your browser only</p>
                          <p>🔐 <strong>Algorithm:</strong> AES-256-CBC with HMAC-SHA256 authentication</p>
                          <p>🌐 <strong>Scope:</strong> Encrypts database queries, results, and session data</p>
                        </div>
                      </div>
                  </div>
                </div>


                {/* OpenAI AI Assistant Settings */}
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center mb-4">
                    <SparklesIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">AI Assistant (OpenAI Integration)</h4>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Description */}
                    <div className="text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="font-medium text-blue-800 dark:text-blue-200 mb-1">🤖 AI-Powered SQL Assistant</p>
                      <p className="text-blue-700 dark:text-blue-300 text-xs leading-relaxed">
                        Enable AI assistance for writing SQL queries using natural language. Configure your OpenAI API key to get 
                        intelligent query suggestions, database schema explanations, and automated SQL generation. 
                        The API key is stored securely in your browser and encrypted alongside your agent communications.
                      </p>
                    </div>

                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="openai-enabled"
                        checked={openaiSettings.enabled}
                        onChange={(e) => {
                          setOpenaiSettings(prev => ({
                            ...prev,
                            enabled: e.target.checked
                          }))
                        }}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label htmlFor="openai-enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Enable AI Assistant for this agent
                      </label>
                    </div>

                    {/* API Key Input */}
                    {openaiSettings.enabled && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <SparklesIcon className="h-4 w-4 inline mr-1" />
                          OpenAI API Key <span className="text-red-500">*</span>
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="password"
                            value={openaiSettings.apiKey}
                            onChange={(e) => {
                              setOpenaiSettings(prev => ({
                                ...prev,
                                apiKey: e.target.value
                              }))
                              setFormErrors(prev => ({ ...prev, openaiApiKey: '' }))
                            }}
                            className={clsx(
                              'flex-1 px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm',
                              formErrors.openaiApiKey ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                            )}
                            placeholder="sk-..."
                          />
                          {openaiSettings.apiKey && (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(openaiSettings.apiKey)
                                  toast.success('API key copied to clipboard')
                                } catch (error) {
                                  console.error('Failed to copy API key:', error)
                                  toast.error('Failed to copy API key to clipboard')
                                }
                              }}
                              className="px-3 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors text-sm"
                              title="Copy API key to clipboard"
                            >
                              <DocumentDuplicateIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        {formErrors.openaiApiKey && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.openaiApiKey}</p>
                        )}
                        
                        {/* Instructions */}
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                            🔑 How to get your OpenAI API Key:
                          </p>
                          <ol className="text-xs text-blue-700 dark:text-blue-300 space-y-1 list-decimal list-inside">
                            <li>Visit <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">platform.openai.com/api-keys</a></li>
                            <li>Sign in to your OpenAI account</li>
                            <li>Click "Create new secret key"</li>
                            <li>Copy the key and paste it above</li>
                          </ol>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                            💰 <strong>Usage:</strong> You will be charged by OpenAI based on your API usage. The AI Assistant uses GPT models for query generation.
                          </p>
                        </div>

                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                          <p>🔒 <strong>Storage:</strong> API key is stored locally in your browser only</p>
                          <p>🤖 <strong>Models:</strong> Uses 4o and 5 models for SQL query generation</p>
                          <p>🔐 <strong>Security:</strong> All AI requests are encrypted using your agent's encryption key</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                      ({(formData.description || '').length}/300 characters, max 4 lines)
                    </span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => {
                      const { value } = e.target;
                      // Limit to 300 characters
                      if (value.length > 300) return;
                      // Count line breaks and limit to 4
                      const lineBreaks = (value.match(/\n/g) || []).length;
                      if (lineBreaks > 3) {
                        // Don't allow more than 4 lines (3 line breaks)
                        const lines = value.split('\n');
                        if (lines.length > 4) {
                          return;
                        }
                      }
                      setFormData({ ...formData, description: value });
                    }}
                    disabled={editingPermission === 'read'}
                    className={clsx(
                      "w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                      editingPermission === 'read' && 'opacity-50 cursor-not-allowed',
                      "border-gray-300 dark:border-gray-600"
                    )}
                    placeholder="Describe what this agent does..."
                    rows={4}
                    maxLength={300}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Max 300 characters and 4 line breaks allowed
                  </p>
                </div>


                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => { setFormData({ ...formData, is_active: e.target.checked }) }}
                    disabled={editingPermission === 'read'}
                    className={clsx(
                      "h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500",
                      editingPermission === 'read' && 'opacity-50 cursor-not-allowed'
                    )}
                  />
                  <label htmlFor="is_active" className={clsx(
                    "ml-3 text-sm text-gray-700 dark:text-gray-300",
                    editingPermission === 'read' && 'opacity-50'
                  )}>
                    {editingAgent ? 'Agent is active' : 'Enable this agent immediately after creation'}
                  </label>
                </div>

                <div className="flex space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button type="button"
                    onClick={() => void handleSaveAgent()}
                    disabled={submitting}
                    className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors shadow-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        {editingAgent ? (editingPermission === 'read' ? 'Saving...' : 'Updating...') : 'Creating...'}
                      </div>
                    ) : (
                      editingAgent ? (editingPermission === 'read' ? 'Save Keys' : 'Update Agent') : 'Create Agent'
                    )}
                  </button>
                  <button type="button"
                    onClick={() => {
                      setShowForm(false)
                      setEditingAgent(null)
                      setEditingPermission('owner')
                    }}
                    disabled={submitting}
                    className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
                
                {/* Validation Error Message - Not shown for read-only users */}
                {showValidationError && Object.keys(formErrors).length > 0 && editingPermission !== 'read' && (
                  <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center">
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2" />
                      <p className="text-red-700 dark:text-red-400 text-sm font-medium">
                        Please fix the missing or invalid fields above before continuing.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
        ) : (
          /* Tab Content */
          <div className="h-full overflow-hidden">
            {activeTab === 'my-agents' && (
              <div className="h-full">
                {loading && allAgents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full p-6">
                    <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-500 border-t-transparent"></div>
                    <p className="text-lg text-gray-500 dark:text-gray-400 mt-4">Loading agents...</p>
                  </div>
                ) : (
                  <div 
                    ref={scrollContainerRef}
                    className="h-full overflow-y-auto p-4"
                  >
            <div className="grid gap-4">
              {allAgents.map((agent) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  currentUser={currentUser}
                  agentVersion={typeof agentVersion === 'string' ? agentVersion : ''}
                  deploymentState={getAgentDeploymentState(agent.id!)}
                  onToggle={handleToggleAgent}
                  onEdit={handleEditAgent}
                  onViewPermissions={handleViewPermissions}
                  onDelete={handleDeleteAgent}
                  onDeploymentStateChange={setAgentDeploymentState}
                  getAgentApiKey={getAgentApiKey}
                  saveAgentApiKey={saveAgentApiKey}
                  getUserPermissionForAgent={getUserPermissionForAgent}
                  formatDate={formatDate}
                  generateDockerCommand={generateDockerCommand}
                  generateDockerComposeConfig={generateDockerComposeConfig}
                  generateKubernetesConfig={generateKubernetesConfig}
                />
              ))}
            </div>

            {allAgents.length === 0 && !loading && (
              <div className="text-center py-12">
                <CpuChipIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No agents found</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {filters.search || filters.status || filters.connectionStatus
                    ? 'No agents match your current filters. Try adjusting your search criteria.'
                    : 'Get started by creating your first database agent.'
                  }
                </p>
                {!filters.search && !filters.status && !filters.connectionStatus && (
                  <button type="button"
                    onClick={handleAddAgent}
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>Create Your First Agent</span>
                  </button>
                )}
              </div>
            )}

            {/* Loading indicator for infinite scroll */}
            {isFetchingMore && (
              <div className="flex items-center justify-center py-6">
                <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-400">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-600 border-t-transparent"></div>
                  <span className="text-sm">Loading more agents...</span>
                </div>
              </div>
            )}
            
            {/* Show total count if we've loaded all agents */}
            {!(storedPagination?.has_next || pagination?.has_next) && allAgents.length > 0 && (
              <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                Showing all {allAgents.length} agents
              </div>
            )}
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'shared-agents' && (
              <div className="p-6">
                <SharedAgentsPanel 
                  refreshTrigger={refreshTrigger}
                  onAgentLeave={handleSharingChange}
                  onSharedAgentsUpdate={handleSharedAgentsUpdate}
                  initialSharedAgents={sharedAgents}
                />
              </div>
            )}
            
            {activeTab === 'invitations' && (
              <div className="p-6">
                <InvitationsPanel 
                  refreshTrigger={refreshTrigger} 
                  onInvitationChange={handleSharingChange}
                  onInvitationsUpdate={handleInvitationsUpdate}
                  initialInvitations={invitations}
                />
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Permissions Modal */}
      <AgentPermissionsModal
        isOpen={showPermissionsModal}
        onClose={() => {
          setShowPermissionsModal(false)
          setAgentToShare(null)
        }}
        agent={agentToShare}
        currentUserPermission={agentToShare ? getUserPermissionForAgent(agentToShare) : 'read'}
        onPermissionsChange={handleSharingChange}
      />
    </div>
    </>
  )
}
